/**
 * @fileoverview Fixture table for the invitation mocks.
 * @description The single source of truth invitation-mock.service.ts reads from. A link is usable
 * only while its account is still INVITED and the link hasn't expired — every other fixture covers
 * a way the real backend answers 400 / appCode 1040 (expired, already used by a registered or
 * active account). Never imported outside this feature's mocks/ folder.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { createChallenge } from './challenge-store'
import { registerMockUser } from './mock-user-directory'

import type { MockChallenge } from './challenge-store'
import type { UserAccountStatus } from '@/shared/lib/auth/auth.types'

export interface MockInvitationRecord {
  userId: string
  email: string
  status: UserAccountStatus
  /** Epoch ms this specific link expires at. */
  expiresAt: number
}

const ONE_HOUR_MS = 60 * 60 * 1000

const INVITATION_RECORDS: Record<string, MockInvitationRecord> = {
  'invited-valid-token': {
    userId: 'invited-nikhil',
    email: 'nikhil@insightbox.tech',
    status: 'INVITED',
    expiresAt: Date.now() + ONE_HOUR_MS,
  },
  'invited-expired-token': {
    userId: 'invited-priya',
    email: 'priya@insightbox.tech',
    status: 'INVITED',
    expiresAt: Date.now() - ONE_HOUR_MS,
  },
  'registered-valid-token': {
    userId: 'invited-arjun',
    email: 'arjun@insightbox.tech',
    status: 'REGISTERED',
    expiresAt: Date.now() + ONE_HOUR_MS,
  },
  'active-token': {
    userId: 'invited-rahul',
    email: 'rahul@insightbox.tech',
    status: 'ACTIVE',
    expiresAt: Date.now() + ONE_HOUR_MS,
  },
}

/**
 * @description Looks up the fixture record for a presented invitation token, only while that link
 * can still be redeemed.
 * @param {string} token - The token from the emailed link.
 * @returns {MockInvitationRecord | undefined} The usable record, or undefined for any link the
 * real backend would refuse with appCode 1040.
 */
export function findUsableInvitation(token: string): MockInvitationRecord | undefined {
  const record = INVITATION_RECORDS[token]

  if (!record || record.status !== 'INVITED' || record.expiresAt <= Date.now()) {
    return undefined
  }

  return record
}

/**
 * @description Redeems an invitation: moves the account to REGISTERED, registers it in the shared
 * mock user directory with the password just chosen (still REGISTERED, so signing in before
 * activating opens an activation code), and opens the activation challenge — mirroring the real
 * backend's accept, which sets the password and emails the activation code in one step.
 * @param {string} token - The token being redeemed.
 * @param {string} newPassword - The password chosen on the set-password form.
 * @returns {MockChallenge | null} The activation challenge, or null for an unknown token.
 */
export function acceptInvitationRecord(token: string, newPassword: string): MockChallenge | null {
  const record = INVITATION_RECORDS[token]

  if (!record) {
    return null
  }

  record.status = 'REGISTERED'
  registerMockUser({
    id: record.userId,
    email: record.email,
    password: newPassword,
    activeRoleKey: 'sales-executive',
    mustChangePassword: false,
    accountStatus: 'REGISTERED',
  })

  return createChallenge(record.userId, 'ACTIVATION')
}
